/**
 * Shared chart renderer for AI chat answers.
 *
 * The LLM emits chart data inside a `[CHART]...[/CHART]` block. This module
 * exposes:
 *   - parseContentWithCharts(content)  → splits answer into text/chart parts
 *   - <ChartRenderer chartData={...} /> → renders one chart from parsed JSON
 *
 * Used by both `ClientFileChatSidebar` and `KBChatPage` so chart rendering
 * stays consistent across every chat surface.
 */
import {
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const CHART_COLORS = [
  '#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6',
  '#ec4899', '#06b6d4', '#f97316', '#14b8a6', '#6366f1',
];

function formatChartValue(value: number) {
  if (Math.abs(value) >= 100) return `$${value.toLocaleString()}`;
  return value.toLocaleString();
}

export interface ChartData {
  type: string;
  title?: string;
  data: any[];
  keys?: string[];
}

export function ChartRenderer({ chartData }: { chartData: ChartData }) {
  const { type, title, data, keys } = chartData;
  if (!data || data.length === 0) return null;

  const normalizedType = type.toLowerCase().replace(/[_\s-]/g, '');
  const chartHeight =
    normalizedType === 'horizontalbar' || normalizedType === 'horizontal' || normalizedType === 'hbar'
      ? Math.max(250, data.length * 30)
      : 280;

  // Auto-detect label key (LLMs often emit `year` / `month` instead of `name`).
  const first = data[0] || {};
  const preferredKeys = ['name', 'label', 'category', 'year', 'fy', 'month', 'period', 'date', 'quarter'];
  let nameKey = 'name';
  for (const k of preferredKeys) { if (k in first) { nameKey = k; break; } }
  if (!(nameKey in first)) {
    for (const k of Object.keys(first)) { if (typeof first[k] === 'string') { nameKey = k; break; } }
  }

  const renderChart = () => {
    switch (normalizedType) {
      case 'pie':
      case 'piechart':
      case 'donut':
      case 'donutchart':
        return (
          <PieChart>
            <Pie data={data} dataKey="value" nameKey={nameKey} cx="35%" cy="50%"
              outerRadius={90} innerRadius={normalizedType === 'donut' || normalizedType === 'donutchart' ? 50 : 0}>
              {data.map((_: any, i: number) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Tooltip formatter={(v: number) => formatChartValue(v)} />
            <Legend layout="vertical" align="right" verticalAlign="middle" iconType="square" wrapperStyle={{ fontSize: '11px' }} />
          </PieChart>
        );

      case 'bar':
      case 'barchart':
        return (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey={nameKey} tick={{ fontSize: 10 }} angle={data.length > 6 ? -30 : 0} textAnchor={data.length > 6 ? 'end' : 'middle'} height={data.length > 6 ? 60 : 30} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
            <Tooltip formatter={(v: number) => formatChartValue(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {keys ? keys.map((key, i) => <Bar key={key} dataKey={key} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[4, 4, 0, 0]} activeBar={false} />)
              : <Bar dataKey="value" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} activeBar={false}>
                  {data.map((_: any, i: number) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Bar>}
          </BarChart>
        );

      case 'stackedbar':
      case 'stacked':
      case 'stackedbarchart':
        return (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey={nameKey} tick={{ fontSize: 10 }} angle={data.length > 6 ? -30 : 0} textAnchor={data.length > 6 ? 'end' : 'middle'} height={data.length > 6 ? 60 : 30} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
            <Tooltip formatter={(v: number) => formatChartValue(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {keys ? keys.map((key, i) => <Bar key={key} dataKey={key} stackId="stack" fill={CHART_COLORS[i % CHART_COLORS.length]} activeBar={false} />)
              : <Bar dataKey="value" stackId="stack" fill={CHART_COLORS[0]} activeBar={false} />}
          </BarChart>
        );

      case 'waterfall':
      case 'waterfallchart': {
        const waterfallData: any[] = [];
        data.forEach((item: any, i: number) => {
          const isTotal = item.isTotal || item.total || (item.name || '').toLowerCase().includes('total') || (item.name || '').toLowerCase().includes('net');
          if (i === 0 || isTotal) {
            waterfallData.push({ ...item, start: 0, end: item.value, fill: isTotal ? '#6366f1' : (item.value >= 0 ? '#22c55e' : '#ef4444') });
          } else {
            const prevEnd = waterfallData[i - 1]?.end || 0;
            waterfallData.push({ ...item, start: prevEnd, end: prevEnd + item.value, fill: item.value >= 0 ? '#22c55e' : '#ef4444' });
          }
        });
        return (
          <BarChart data={waterfallData}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey={nameKey} tick={{ fontSize: 10 }} angle={data.length > 5 ? -30 : 0} textAnchor={data.length > 5 ? 'end' : 'middle'} height={data.length > 5 ? 60 : 30} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
            <Tooltip formatter={(v: number, name: string) => [formatChartValue(Math.abs(v)), name === 'start' ? 'Base' : 'Value']} />
            <Bar dataKey="start" stackId="waterfall" fill="transparent" activeBar={false} />
            <Bar dataKey="value" stackId="waterfall" radius={[4, 4, 0, 0]} activeBar={false}>
              {waterfallData.map((d: any, i: number) => <Cell key={i} fill={d.fill} />)}
            </Bar>
          </BarChart>
        );
      }

      case 'horizontalbar':
      case 'horizontal':
      case 'hbar':
        return (
          <BarChart data={data} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
            <YAxis type="category" dataKey={nameKey} tick={{ fontSize: 10 }} width={120} />
            <Tooltip formatter={(v: number) => formatChartValue(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {keys ? keys.map((key, i) => <Bar key={key} dataKey={key} fill={CHART_COLORS[i % CHART_COLORS.length]} activeBar={false} />)
              : <Bar dataKey="value" fill={CHART_COLORS[0]} radius={[0, 4, 4, 0]} activeBar={false} />}
          </BarChart>
        );

      case 'line':
      case 'linechart':
        return (
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey={nameKey} tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
            <Tooltip formatter={(v: number) => formatChartValue(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {keys ? keys.map((key, i) => <Line key={key} type="monotone" dataKey={key} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />)
              : <Line type="monotone" dataKey="value" stroke={CHART_COLORS[0]} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />}
          </LineChart>
        );

      case 'area':
      case 'areachart':
        return (
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey={nameKey} tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
            <Tooltip formatter={(v: number) => formatChartValue(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {keys ? keys.map((key, i) => <Line key={key} type="monotone" dataKey={key} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 4 }} />)
              : <Line type="monotone" dataKey="value" stroke={CHART_COLORS[0]} strokeWidth={2} dot={{ r: 4 }} />}
          </LineChart>
        );

      case 'scatter':
      case 'scatterchart':
      case 'dot':
      case 'dotchart':
      case 'dots':
      case 'bubble':
        return (
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey={nameKey} tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
            <Tooltip formatter={(v: number) => formatChartValue(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {keys ? keys.map((key, i) => <Line key={key} type="monotone" dataKey={key} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={0} dot={{ r: 6, fill: CHART_COLORS[i % CHART_COLORS.length] }} />)
              : <Line type="monotone" dataKey="value" stroke={CHART_COLORS[0]} strokeWidth={0} dot={{ r: 6, fill: CHART_COLORS[0] }} />}
          </LineChart>
        );

      default:
        return (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey={nameKey} tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip formatter={(v: number) => formatChartValue(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="value" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} activeBar={false} />
          </BarChart>
        );
    }
  };

  return (
    <div className="my-3 p-3 bg-muted/30 rounded-lg border">
      {title && <h4 className="text-sm font-semibold mb-2 text-center">{title}</h4>}
      <ResponsiveContainer width="100%" height={chartHeight}>
        {renderChart()}
      </ResponsiveContainer>
    </div>
  );
}

export type ChatPart =
  | { type: 'text'; content: string }
  | { type: 'chart'; content: ''; chartData: ChartData };

/**
 * Split an LLM answer into alternating text + chart blocks.
 * Charts are emitted by the LLM as `[CHART]{json}[/CHART]` (optionally
 * inside a ```json fence). Anything else stays as text.
 */
export function parseContentWithCharts(content: string): ChatPart[] {
  const parts: ChatPart[] = [];
  // Accept both the canonical `[CHART]…[/CHART]` AND the common LLM mistake
  // of omitting the closing tag - in that case scan for a balanced JSON
  // value after `[CHART]`.
  const openTag = /(?:```(?:json)?\s*)?\[CHART\]/g;
  let lastIndex = 0;
  let m: RegExpExecArray | null;

  while ((m = openTag.exec(content)) !== null) {
    if (m.index > lastIndex) {
      parts.push({ type: 'text', content: content.slice(lastIndex, m.index) });
    }
    const afterOpen = openTag.lastIndex;
    const closeIdx = content.indexOf('[/CHART]', afterOpen);
    let chartBody: string | null = null;
    let consumedTo = afterOpen;

    if (closeIdx !== -1) {
      chartBody = content.slice(afterOpen, closeIdx).trim();
      consumedTo = closeIdx + '[/CHART]'.length;
      const tail = content.slice(consumedTo).match(/^\s*```/);
      if (tail) consumedTo += tail[0].length;
    } else {
      const tail = content.slice(afterOpen);
      const firstBrace = tail.search(/\{|\[/);
      if (firstBrace !== -1) {
        let depth = 0;
        let i = firstBrace;
        let inString = false;
        let escape = false;
        const opener = tail[i];
        const closer = opener === '{' ? '}' : ']';
        for (; i < tail.length; i++) {
          const ch = tail[i];
          if (escape) { escape = false; continue; }
          if (ch === '\\') { escape = true; continue; }
          if (ch === '"') { inString = !inString; continue; }
          if (inString) continue;
          if (ch === opener) depth++;
          else if (ch === closer) { depth--; if (depth === 0) { i++; break; } }
        }
        chartBody = tail.slice(firstBrace, i).trim();
        consumedTo = afterOpen + i;
      }
    }

    if (chartBody) {
      try {
        const chartData = JSON.parse(chartBody);
        parts.push({ type: 'chart', content: '', chartData });
      } catch {
        parts.push({ type: 'text', content: content.slice(m.index, consumedTo) });
      }
    } else {
      parts.push({ type: 'text', content: content.slice(m.index, afterOpen) });
    }
    lastIndex = consumedTo;
    openTag.lastIndex = consumedTo;
  }
  if (lastIndex < content.length) {
    parts.push({ type: 'text', content: content.slice(lastIndex) });
  }
  return parts;
}

export const CHART_DIRECTIVE_PROMPT = `When the user asks you to draw, show, or visualize a chart (pie, bar, line, donut, stacked bar, horizontal bar, waterfall, area, scatter), output the chart inside a single block with this EXACT format:

[CHART]
{"type":"pie","title":"Chart Title","data":[{"name":"Label1","value":100},{"name":"Label2","value":200}]}
[/CHART]

CRITICAL FORMATTING RULES - failure to follow these means the chart will NOT render:
1. The block MUST start with the literal token \`[CHART]\` on its own and end with the literal token \`[/CHART]\`. Both tags are MANDATORY.
2. EVERY data point MUST have a field called \`"name"\` for the x-axis / pie slice label. Do NOT use \`"year"\`, \`"month"\`, \`"period"\` etc. as the label key - use \`"name"\`. Example: \`{"name":"FY2024","value":500000}\`, NOT \`{"year":"FY2024","value":500000}\`.
3. For multi-series bar/line/stackedBar: \`{"type":"stackedBar","title":"Revenue vs Expenses","data":[{"name":"FY2024","revenue":500000,"expenses":300000}],"keys":["revenue","expenses"]}\`
4. For waterfall: each item has \`value\`, mark final aggregate items with \`"isTotal":true\`.

Supported types: "pie", "donut", "bar", "stackedBar", "horizontalBar", "line", "area", "dot", "waterfall".
- Use "horizontalBar" when there are >6 categories so labels fit.
- Use "donut" instead of "pie" for a cleaner look.
- Always include a brief text explanation alongside the chart.
- Only emit a chart when the user explicitly asks for a visual / chart / graph.`;
